import type { CalculatorTab } from '../types/mortgageTypes';

const FREQUENCY_PAYMENT_LABELS: Record<string, string> = {
  monthly: 'MONTHLY',
  'semi-monthly': 'SEMI-MONTHLY',
  'bi-weekly': 'BI-WEEKLY',
  'accelerated-bi-weekly': 'ACCELERATED BI-WEEKLY',
  weekly: 'WEEKLY',
  'accelerated-weekly': 'ACCELERATED WEEKLY',
};

/** Uppercase label for the live payment header, e.g. "MONTHLY PAYMENT". */
export function getPaymentFrequencyPaymentLabel(frequency?: string): string {
  const key = frequency || 'monthly';
  const word =
    FREQUENCY_PAYMENT_LABELS[key] ?? key.replace(/-/g, ' ').toUpperCase();
  return `${word} PAYMENT`;
}

/**
 * Total monthly payment shown in KeyInsights and the mobile live payment bar.
 * schedule[0].payment is monthly-bucket P+I; expenses use expenseFactor 0.5 for
 * sub-monthly payment frequencies (matching KeyInsights display convention).
 */
export function getDisplayedMonthlyPayment(tab: CalculatorTab | undefined): number | null {
  if (!tab?.data?.amortizationSchedule?.length) return null;

  const { mortgageTerms, annualExpenses, amortizationSchedule } = tab.data;
  if (!mortgageTerms || !annualExpenses) return null;

  const firstEntry = amortizationSchedule[0];
  const isSubMonthly =
    !!mortgageTerms.paymentFrequency && mortgageTerms.paymentFrequency !== 'monthly';
  const expenseFactor = isSubMonthly ? 0.5 : 1;
  const hp = mortgageTerms.homePrice;

  const propertyTaxMo =
    annualExpenses.propertyTaxes.type === 'amount'
      ? annualExpenses.propertyTaxes.value / 12
      : (hp * annualExpenses.propertyTaxes.value) / 100 / 12;

  const homeInsMo =
    annualExpenses.homeInsurance.type === 'amount'
      ? annualExpenses.homeInsurance.value
      : (hp * annualExpenses.homeInsurance.value) / 100 / 12;

  const hoaMo =
    annualExpenses.hoaUtilities.type === 'amount'
      ? annualExpenses.hoaUtilities.value
      : (hp * annualExpenses.hoaUtilities.value) / 100 / 12;

  return firstEntry.payment + (propertyTaxMo + homeInsMo + hoaMo) * expenseFactor;
}
